import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DollarSignIcon, LockIcon, MapIcon, TableIcon, UnlockIcon, UsersIcon } from 'lucide-react';
import { TrackerState, User, LicenseData, MichiganLicenseData, StateComplianceData, US_STATES, DEFAULT_OFFICERS, createDefaultLicense, createDefaultMichiganLicense, createDefaultStateCompliance, getUserLicensedStateCount } from './types';
import { CompanyAccessModal } from './CompanyAccessModal';
import { CompanyStateComplianceModal } from './CompanyStateComplianceModal';
import { CoverageMap } from './CoverageMap';
import { UserLicenseModal } from './UserLicenseModal';
import { StateLicenseModal } from './StateLicenseModal';
import { StateModal } from './StateModal';
import { MichiganModal } from './MichiganModal';
import { LicenseMatrix } from './LicenseMatrix';
const STORAGE_KEY = 'nmls-license-tracker-v3';
const COMPANY_ACCESS_KEY = 'nmls-company-access-v1';
const ORIGINAL_DEFAULT_OFFICER_NAMES: Record<string, string> = {
  'lo-01': 'Donovan Jappaya',
  'lo-02': 'Donovan Salem',
  'lo-03': 'Fadia Salem',
  'lo-04': 'Hayden Denha',
  'lo-05': 'Jaden Jappaya',
  'lo-06': 'Johnathan Denha',
  'lo-07': 'Lance Ibrahim',
  'lo-08': 'Max Fazio',
  'lo-09': 'Nikol Kadilli',
  'lo-10': 'Paul Shango',
  'lo-11': 'Saber Denha',
  'lo-12': 'Sarah Batayeh',
  'lo-13': 'Sharla Kalinowski'
};
function migrateDefaultOfficerNames(users: User[]): User[] {
  const namesById = new Map(DEFAULT_OFFICERS.map(officer => [officer.id, officer.name]));
  return users.map(user => {
    const nextName = namesById.get(user.id);
    const originalName = ORIGINAL_DEFAULT_OFFICER_NAMES[user.id];
    if (nextName && user.name === originalName) {
      return {
        ...user,
        name: nextName
      };
    }
    return user;
  });
}
function getInitialState(): TrackerState {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as TrackerState;
      if (Array.isArray(parsed.users) && typeof parsed.licenses === 'object') {
        return {
          users: migrateDefaultOfficerNames(parsed.users),
          licenses: parsed.licenses || {},
          michiganLicenses: parsed.michiganLicenses || {},
          stateCompliance: parsed.stateCompliance || {}
        };
      }
    }
  } catch {
    // Fall through
  }
  return {
    users: DEFAULT_OFFICERS,
    licenses: {},
    michiganLicenses: {},
    stateCompliance: {}
  };
}
export function LicenseTracker() {
  const [state, setState] = useState<TrackerState>(getInitialState);
  // Modal state
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedStateName, setSelectedStateName] = useState<string | null>(null);
  // Detail modal: specific user + state
  const [detailUserId, setDetailUserId] = useState<string | null>(null);
  const [detailStateName, setDetailStateName] = useState<string | null>(null);
  // Company-level detail modal
  const [companyStateName, setCompanyStateName] = useState<string | null>(null);
  // Active view: officer grid or company coverage map
  const [view, setView] = useState<'grid' | 'map'>('grid');
  // Private company access (owner only)
  const [isCompanyUnlocked, setIsCompanyUnlocked] = useState(false);
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [companyPasscode, setCompanyPasscode] = useState<string | null>(() => {
    try {
      return localStorage.getItem(COMPANY_ACCESS_KEY);
    } catch {
      return null;
    }
  });
  const handleLockCompanyView = useCallback(() => {
    setIsCompanyUnlocked(false);
    setCompanyStateName(null);
    setView('grid');
  }, []);
  const handleCreatePasscode = useCallback((passcode: string) => {
    try {
      localStorage.setItem(COMPANY_ACCESS_KEY, passcode);
    } catch {
      // Ignore storage failures — access still unlocks for this session
    }
    setCompanyPasscode(passcode);
    setIsCompanyUnlocked(true);
    setIsAccessModalOpen(false);
  }, []);
  const handleVerifyPasscode = useCallback((passcode: string) => {
    if (passcode !== companyPasscode) return false;
    setIsCompanyUnlocked(true);
    setIsAccessModalOpen(false);
    return true;
  }, [companyPasscode]);
  const handleResetPasscode = useCallback(() => {
    try {
      localStorage.removeItem(COMPANY_ACCESS_KEY);
    } catch {
      // Ignore storage failures
    }
    setCompanyPasscode(null);
    setIsCompanyUnlocked(false);
  }, []);
  // Persist
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);
  // --- License Updates ---
  const handleUpdateLicense = useCallback((userId: string, stateName: string, license: LicenseData) => {
    setState(prev => ({
      ...prev,
      licenses: {
        ...prev.licenses,
        [userId]: {
          ...(prev.licenses[userId] || {}),
          [stateName]: license
        }
      }
    }));
  }, []);
  const handleUpdateMichiganLicense = useCallback((userId: string, license: MichiganLicenseData) => {
    setState(prev => ({
      ...prev,
      michiganLicenses: {
        ...prev.michiganLicenses,
        [userId]: license
      }
    }));
  }, []);
  const handleUpdateStateCompliance = useCallback((stateName: string, compliance: StateComplianceData) => {
    setState(prev => ({
      ...prev,
      stateCompliance: {
        ...prev.stateCompliance,
        [stateName]: compliance
      }
    }));
  }, []);
  const handleAddUserToState = useCallback((userId: string, stateName: string) => {
    if (stateName === 'Michigan') {
      setState(prev => {
        if (prev.michiganLicenses[userId]) return prev;
        return {
          ...prev,
          michiganLicenses: {
            ...prev.michiganLicenses,
            [userId]: createDefaultMichiganLicense()
          }
        };
      });
    } else {
      setState(prev => {
        if (prev.licenses[userId]?.[stateName]) return prev;
        return {
          ...prev,
          licenses: {
            ...prev.licenses,
            [userId]: {
              ...(prev.licenses[userId] || {}),
              [stateName]: createDefaultLicense()
            }
          }
        };
      });
    }
  }, []);
  // --- Open detail for user+state ---
  const handleOpenUserStateDetail = useCallback((userId: string, stateName: string) => {
    setDetailUserId(userId);
    setDetailStateName(stateName);
  }, []);
  // --- Calculations ---
  const stats = useMemo(() => {
    let totalLicensed = 0;
    let totalInProgress = 0;
    let totalCost = 0;
    for (const user of state.users) {
      const counts = getUserLicensedStateCount(user.id, state.licenses, state.michiganLicenses);
      totalLicensed += counts.licensed;
      totalInProgress += counts.inProgress;
      // Sum costs from regular licenses
      const userLicenses = state.licenses[user.id] || {};
      for (const lic of Object.values(userLicenses)) {
        // Use charges if available, fall back to legacy cost field
        const chargesTotal = (lic.charges || []).reduce((a, c) => a + c.amount, 0);
        totalCost += chargesTotal > 0 ? chargesTotal : lic.cost || 0;
        totalCost += lic.renewals.reduce((a, r) => a + (r.fee || 0), 0);
      }
      // Sum costs from Michigan
      const mi = state.michiganLicenses[user.id];
      if (mi) {
        totalCost += mi.stepCosts.reduce((a, c) => a + (c || 0), 0);
        totalCost += mi.renewals.reduce((a, r) => a + (r.fee || 0), 0);
      }
    }
    return {
      totalLicensed,
      totalInProgress,
      totalCost,
      totalUsers: state.users.length
    };
  }, [state]);
  // --- Find user/license for detail modal ---
  const detailUser = detailUserId ? state.users.find(u => u.id === detailUserId) || null : null;
  const selectedUser = selectedUserId ? state.users.find(u => u.id === selectedUserId) || null : null;
  return <div className="h-screen w-full bg-[#0a0f1e] text-slate-200 flex flex-col overflow-hidden" data-id="element-227" data-mp="1c9iz1glhhq0s">
      <div className="h-px w-full bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent flex-shrink-0" data-id="element-228" data-mp="1h71bhgypmndj" />

      <div className="flex flex-col flex-1 min-h-0 px-4 py-3" data-id="element-229" data-mp="1m88k1i8ofql9">
        {/* Header */}
        <header className="flex-shrink-0 mb-3" data-id="element-230" data-mp="1q7zfw0d28wwn">
          <div className="flex items-center justify-between flex-wrap gap-3" data-id="element-231" data-mp="1v7c1khs73xxn">
            {/* View Switcher */}
            <div className="flex items-center gap-1 rounded-lg border border-slate-700/40 bg-slate-800/40 p-0.5" role="tablist" aria-label="Tracker view" data-id="element-232" data-mp="1zxshq9nehxnp">
              <button role="tab" aria-selected={view === 'grid'} onClick={() => setView('grid')} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${view === 'grid' ? 'bg-slate-600/80 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-233" data-mp="29xazqkjr39d4">
                <TableIcon className="h-3.5 w-3.5" data-id="element-234" data-mp="3phai0rwxhz8q" />
                Grid
              </button>
              {isCompanyUnlocked && <button role="tab" aria-selected={view === 'map'} onClick={() => setView('map')} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${view === 'map' ? 'bg-slate-600/80 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-235" data-mp="lz8qozbuxvs6">
                  <MapIcon className="h-3.5 w-3.5" data-id="element-236" data-mp="14rbd6rb6k6ac" />
                  Coverage Map
                </button>}
            </div>

            {/* Stats Row */}
            <div className="flex flex-wrap items-center gap-2" data-id="element-237" data-mp="2x9dk3vu8nvc1">
              <div className="flex items-center gap-2 bg-slate-900/50 border border-slate-800 rounded-lg px-3 py-1.5" data-id="element-238" data-mp="2sa0tr145s64p">
                <UsersIcon className="w-3.5 h-3.5 text-slate-500" data-id="element-239" data-mp="377t6jlibmgf7" />
                <span className="text-xs text-slate-400" data-id="element-240" data-mp="327yg1j2gjsuo">
                  <span className="text-white font-bold text-sm tabular-nums" data-id="element-241" data-mp="326do817j2o0i">
                    {stats.totalUsers}
                  </span>{' '}
                  officers
                </span>
              </div>

              <div className="flex items-center gap-2 bg-slate-900/50 border border-slate-800 rounded-lg px-3 py-1.5" data-id="element-242" data-mp="3b6iu26x2peck">
                <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.8)]" data-id="element-243" data-mp="3kyg9jpv46sh3" />
                <span className="text-xs text-slate-400" data-id="element-244" data-mp="2s1x5vchuqkt">
                  <span className="text-white font-bold text-sm tabular-nums" data-id="element-245" data-mp="2qh284m9p5mf">
                    {stats.totalLicensed}
                  </span>{' '}
                  licensed
                </span>
              </div>

              {stats.totalInProgress > 0 && <div className="flex items-center gap-2 bg-slate-900/50 border border-slate-800 rounded-lg px-3 py-1.5" data-id="element-246" data-mp="md24619kj269">
                  <div className="w-2 h-2 rounded-full bg-amber-500 shadow-[0_0_6px_rgba(245,158,11,0.8)]" data-id="element-247" data-mp="8eaucr1qo2nx" />
                  <span className="text-xs text-slate-400" data-id="element-248" data-mp="dfrqyad6x2yp">
                    <span className="text-white font-bold text-sm tabular-nums" data-id="element-249" data-mp="3ut3ufz4q72ua">
                      {stats.totalInProgress}
                    </span>{' '}
                    in progress
                  </span>
                </div>}

              <div className="flex items-center gap-2 bg-slate-900/50 border border-slate-800 rounded-lg px-3 py-1.5" data-id="element-250" data-mp="3pv22eopbnt6o">
                <DollarSignIcon className="w-3.5 h-3.5 text-emerald-400" data-id="element-251" data-mp="3burbjlo0ieli" />
                <span className="text-xs text-slate-400" data-id="element-252" data-mp="3bxnc7hr8vhdn">
                  <span className="text-white font-bold text-sm tabular-nums" data-id="element-253" data-mp="3gt2nq31s516w">
                    ${stats.totalCost.toLocaleString()}
                  </span>{' '}
                  invested
                </span>
              </div>

              <button onClick={() => isCompanyUnlocked ? handleLockCompanyView() : setIsAccessModalOpen(true)} className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${isCompanyUnlocked ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border-slate-800 bg-slate-900/50 text-slate-400 hover:text-slate-200'}`} data-id="element-254" data-mp="1hlm5qnlczmxu">
                {isCompanyUnlocked ? <UnlockIcon className="h-3.5 w-3.5" data-id="element-255" data-mp="kx6e8lyov3i3" /> : <LockIcon className="h-3.5 w-3.5" data-id="element-256" data-mp="kv3eha7qhw06" />}
                Company view {isCompanyUnlocked ? 'unlocked' : 'locked'}
              </button>
            </div>
          </div>
        </header>

        {view === 'grid' || !isCompanyUnlocked ? <LicenseMatrix users={state.users} licenses={state.licenses} michiganLicenses={state.michiganLicenses} stateCompliance={state.stateCompliance} showCompanyRow={isCompanyUnlocked} onCellClick={handleOpenUserStateDetail} onCompanyStateClick={setCompanyStateName} onUserClick={setSelectedUserId} data-id="element-257" data-mp="au13pwuaz27i" /> : <CoverageMap users={state.users} licenses={state.licenses} michiganLicenses={state.michiganLicenses} stateCompliance={state.stateCompliance} onStateClick={setCompanyStateName} data-id="element-258" data-mp="thwcf2b25ptr" />}
      </div>

      {/* --- Modals --- */}

      {/* Private company access gate */}
      <CompanyAccessModal isOpen={isAccessModalOpen} hasPasscode={!!companyPasscode} onClose={() => setIsAccessModalOpen(false)} onCreatePasscode={handleCreatePasscode} onVerifyPasscode={handleVerifyPasscode} onResetPasscode={handleResetPasscode} data-id="element-259" data-mp="358rw7te2wtb0" />

      {/* Company Compliance Detail (click Olympian Mortgage state) */}
      {companyStateName && isCompanyUnlocked && <CompanyStateComplianceModal stateName={companyStateName} isOpen={true} onClose={() => setCompanyStateName(null)} compliance={state.stateCompliance[companyStateName] || createDefaultStateCompliance()} onUpdateCompliance={compliance => {
      handleUpdateStateCompliance(companyStateName, compliance);
    }} data-id="element-260" data-mp="13nw88d9khzsh" />}

      {/* User License Overview (click user → see all their states) */}
      {selectedUser && <UserLicenseModal user={selectedUser} isOpen={true} onClose={() => setSelectedUserId(null)} licenses={state.licenses[selectedUser.id] || {}} michiganLicense={state.michiganLicenses[selectedUser.id]} onOpenState={stateName => {
      handleOpenUserStateDetail(selectedUser.id, stateName);
    }} data-id="element-261" data-mp="3t9k4tv13g5cm" />}

      {/* State License Overview (click state → see all users in that state) */}
      {selectedStateName && !selectedUserId && <StateLicenseModal stateName={selectedStateName} isOpen={true} onClose={() => setSelectedStateName(null)} users={state.users} licenses={state.licenses} michiganLicenses={state.michiganLicenses} onUpdateLicense={handleUpdateLicense} onUpdateMichiganLicense={handleUpdateMichiganLicense} onAddUserToState={handleAddUserToState} onOpenUserStateDetail={handleOpenUserStateDetail} data-id="element-262" data-mp="2nj1s1oxf0j0i" />}

      {/* Detail: specific user + specific state */}
      {detailUser && detailStateName && detailStateName !== 'Michigan' && <StateModal user={detailUser} stateName={detailStateName} isOpen={true} onClose={() => {
      setDetailUserId(null);
      setDetailStateName(null);
    }} license={state.licenses[detailUser.id]?.[detailStateName] || createDefaultLicense()} onUpdateLicense={license => {
      handleUpdateLicense(detailUser.id, detailStateName!, license);
    }} data-id="element-263" data-mp="n14ahe8yliir" />}

      {/* Detail: specific user + Michigan */}
      {detailUser && detailStateName === 'Michigan' && <MichiganModal user={detailUser} isOpen={true} onClose={() => {
      setDetailUserId(null);
      setDetailStateName(null);
    }} license={state.michiganLicenses[detailUser.id] || createDefaultMichiganLicense()} onUpdateLicense={license => {
      handleUpdateMichiganLicense(detailUser.id, license);
    }} data-id="element-264" data-mp="3rhf7n1r4aqz3" />}
    </div>;
}